import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useFlightRestrictionZonesQuery } from "@/api/v0/flight-restriction-zones";
import { findZoneBySlug, extractZoneData, formatRestriction } from "@/lib/zone-utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "@tanstack/react-router";
import { 
  MapPin, 
  ArrowLeft, 
  ExternalLink,
  AlertTriangle,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";

export const FlightRestrictionZoneMapRoute = () => {
  const { zoneSlug } = useParams({ from: "/flight-restriction-zones/$zoneSlug/map" });
  const navigate = useNavigate();
  const [copiedCoords, setCopiedCoords] = useState(false);
  
  const { 
    data: zonesData, 
    isLoading, 
    error 
  } = useFlightRestrictionZonesQuery();

  // Find the zone from the paginated response
  const foundZone = zonesData?.zones ? findZoneBySlug(zonesData.zones, zoneSlug) : null;
  const zone = useMemo(() => foundZone ? {
    id: foundZone.frz_id || 0,
    name: foundZone.name || '',
    type: foundZone.type || 'Unknown',
    source: foundZone.source || '',
    version: foundZone.version || '',
    effective_from: foundZone.effective_from || '',
    effective_to: foundZone.effective_to || null,
    active: foundZone.active || false,
    last_updated: foundZone.last_updated || '',
    h3_indexes: foundZone.h3_indexes || [],
    data: foundZone.data || {},
    geometry: foundZone.geometry || '',
  } : null, [foundZone]);
  const zoneData = zone?.data ? extractZoneData(zone.data) : null;

  useEffect(() => {
    if (!isLoading && !zone && zonesData?.zones) {
      navigate({ to: '/flight-restriction-zones' });
    }
  }, [isLoading, zone, zonesData]);

  const handleCopyCoordinates = async () => {
    if (!zone) return;
    
    try {
      // Geometry is now a string, so we need to parse it if it exists
      let coordinates: string = "";
      
      if (zone.geometry && typeof zone.geometry === 'string' && zone.geometry.length > 0) {
        try {
          const geom = JSON.parse(zone.geometry);
          if (geom.type === "Polygon") {
            const coords = geom.coordinates?.[0] as number[][];
            if (coords) {
              coordinates = coords.map((coord: number[]) => `${coord[1]},${coord[0]}`).join(";");
            }
          } else if (geom.type === "MultiPolygon") {
            const coords = geom.coordinates?.[0]?.[0] as number[][];
            if (coords) {
              coordinates = coords.map((coord: number[]) => `${coord[1]},${coord[0]}`).join(";");
            }
          }
        } catch {
          toast.error("Invalid geometry data");
          return;
        }
      }
      
      if (!coordinates) {
        toast.error("No coordinates available");
        return;
      }
      
      await navigator.clipboard.writeText(coordinates);
      setCopiedCoords(true);
      toast.success("Coordinates copied to clipboard");
      setTimeout(() => setCopiedCoords(false), 2000);
    } catch {
      toast.error("Failed to copy coordinates");
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Prohibited":
      case "Restricted":
      case "Danger":
        return "destructive";
      default:
        return "outline";
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !zone) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/flight-restriction-zones">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Zones
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Zone not found</h3>
              <p className="text-muted-foreground mb-4">
                The flight restriction zone you're looking for doesn't exist or has been removed.
              </p>
              <Button asChild>
                <Link to="/flight-restriction-zones">View All Zones</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Flight Restrictions", href: "/flight-restriction-zones" },
    { label: zone?.name || "Zone", href: `/flight-restriction-zones/${zoneSlug}` },
    { label: "Map View", href: `/flight-restriction-zones/${zoneSlug}/map` },
  ];

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbs} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Link to={`/flight-restriction-zones/${zoneSlug}` as any}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Zone
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <MapPin className="h-6 w-6" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{zone.name}</h1>
              <p className="text-muted-foreground">
                Map view for flight restriction zone
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getTypeColor(zone.type)}>
            {zone.type}
          </Badge>
          <Badge variant={zone.active ? "default" : "secondary"}>
            {zone.active ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Map */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Interactive Map
              </CardTitle>
              <CardDescription>
                Zone boundaries and location details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-96 bg-muted rounded-md flex items-center justify-center relative overflow-hidden">
                {/* Map placeholder - in a real implementation, this would be an actual map component */}
                <div className="text-center">
                  <MapPin className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Interactive map view</p>
                  <p className="text-sm text-muted-foreground">
                    Zone: {zone.name} ({zone.type})
                  </p>
                </div>
                
                {/* Map controls overlay */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const mapUrl = `${window.location.origin}/map?highlight-zone=${zone.id}&zoom=12`;
                      window.open(mapUrl, '_blank');
                    }}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in Map
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyCoordinates}
                    className="flex items-center gap-2"
                  >
                    <MapPin className="h-4 w-4" />
                    {copiedCoords ? "Copied!" : "Copy Coords"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Zone Information */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Zone Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Type</label>
                <p className="text-sm">
                  <Badge variant={getTypeColor(zone.type)} className="text-xs">
                    {zone.type}
                  </Badge>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Status</label>
                <p className="text-sm">
                  <Badge variant={zone.active ? "default" : "secondary"} className="text-xs">
                    {zone.active ? "Active" : "Inactive"}
                  </Badge>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Source</label>
                <p className="text-sm">{zone.source}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Geometry Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">H3 Indexes</label>
                <p className="text-sm">{zone.h3_indexes?.length || 0} spatial indexes</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Effective From</label>
                <p className="text-sm">{new Date(zone.effective_from).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Effective To</label>
                <p className="text-sm">
                  {zone.effective_to ? new Date(zone.effective_to).toLocaleDateString() : "No expiry"}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
              <CardDescription>
                Common actions for this zone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const mapUrl = `${window.location.origin}/map?highlight-zone=${zone.id}&zoom=12`;
                    window.open(mapUrl, '_blank');
                  }}
                  className="flex items-center gap-2 justify-start"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in Full Map
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCopyCoordinates}
                  className="flex items-center gap-2 justify-start"
                >
                  <MapPin className="h-4 w-4" />
                  {copiedCoords ? "Copied!" : "Copy Coordinates"}
                </Button>
                <Button variant="outline" asChild className="flex items-center gap-2 justify-start">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  <Link to={`/flight-restriction-zones/${zoneSlug}/violations` as any}>
                    <AlertTriangle className="h-4 w-4" />
                    View Violations
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Zone Restrictions */}
          {zoneData && zoneData.restrictions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Zone Restrictions
                </CardTitle>
                <CardDescription>
                  Detailed restriction information for this zone
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {zoneData.restrictions.map((restriction, index) => {
                    const formatted = formatRestriction(restriction);
                    return (
                      <div key={index} className="border rounded-lg p-4 space-y-2">
                        <div className="font-medium text-sm">
                          {formatted.formattedDescription}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-muted-foreground">Upper Limit:</span>
                            <span className="ml-2">{restriction.upperLimit}</span>
                          </div>
                          <div>
                            <span className="font-medium text-muted-foreground">Lower Limit:</span>
                            <span className="ml-2">{restriction.lowerLimit}</span>
                          </div>
                          {formatted.hasClass && (
                            <div className="col-span-2">
                              <span className="font-medium text-muted-foreground">Class:</span>
                              <span className="ml-2">{restriction.class}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
